import React, { useState } from 'react';
import { Trash, FileText } from 'lucide-react';
import { Button } from "../../../../components/ui/button";
import { useDeletePodByIdActionMutation } from '../../../../common/reducers/backend-api/backend-k8s-api';
import DeletePodDialog from './DeletePodDialog';
import LogsDialog from './LogsDialog';
import { toast } from 'sonner';

// Function to delete pod using kubectl proxy (client-side)
const deletePodViaKubectlProxy = async (podName: string, namespace: string, gracePeriodSeconds = 30) => {
  try {
    // The kubectl proxy should be running at the default localhost:8001
    const proxyUrl = `/api/v1/namespaces/${namespace}/pods/${podName}`;
    
    // Use fetch API to make a DELETE request to the kubectl proxy
    const response = await fetch(proxyUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      // Include gracePeriodSeconds for graceful deletion
      body: JSON.stringify({
        apiVersion: 'v1',
        kind: 'DeleteOptions',
        gracePeriodSeconds: gracePeriodSeconds,
        propagationPolicy: 'Foreground', // Ensure dependent objects are deleted
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to delete pod: ${errorData.message || response.statusText}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error deleting pod via kubectl proxy:', error);
    throw error;
  }
};

interface PodActionsProps {
  podName: string;
  namespace: string;
  className?: string;
}

const PodActions: React.FC<PodActionsProps> = ({
  podName,
  namespace,
  className = ''
}) => {
  // Fallback to the existing API if needed
  const [deletePodById] = useDeletePodByIdActionMutation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLogsDialogOpen, setIsLogsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle initiating pod deletion
  const handleDeletePod = () => {
    setIsDeleteDialogOpen(true);
  };

  // Handle confirm pod deletion
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      // First try to use the kubectl proxy for graceful deletion
      try {
        await deletePodViaKubectlProxy(podName, namespace, 30);
        toast.success(`Pod ${podName} is being gracefully deleted.`);
      } catch (proxyError) {
        console.error('Failed to delete pod via kubectl proxy:', proxyError);
        
        // Fallback to the API if the kubectl proxy method fails
        toast.info("Kubectl proxy unavailable, using API deletion...");
        
        await deletePodById(podName);
      }
      
      // The Watch API will automatically update the UI - no callback needed
    } catch (error) {
      console.error('Failed to delete pod:', error);
      toast.error(`Failed to delete pod: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  // Handle opening logs dialog
  const handleViewLogs = () => {
    setIsLogsDialogOpen(true);
  };

  return (
    <div className={`flex space-x-1 ${className}`}>
      {/* View Logs Button */}
      <Button 
        variant="ghost" 
        size="icon" 
        title="View logs"
        onClick={handleViewLogs}
        className="text-primary hover:text-primary hover:bg-primary/10"
      >
        <FileText className="h-4 w-4" />
      </Button>
      
      {/* Delete Pod Button */}
      <Button 
        variant="ghost" 
        size="icon" 
        title="Delete pod"
        onClick={handleDeletePod}
        className="text-destructive hover:text-destructive hover:bg-destructive/10"
      >
        <Trash className="h-4 w-4" />
      </Button>

      {/* Delete Pod Confirmation Dialog */}
      <DeletePodDialog
        open={isDeleteDialogOpen}
        podName={podName}
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />

      {/* Pod Logs Dialog */}
      <LogsDialog
        open={isLogsDialogOpen}
        podName={podName}
        namespace={namespace}
        onClose={() => setIsLogsDialogOpen(false)}
      />
    </div>
  );
};

export default PodActions; 